import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { VendorBadgeCollection } from '../../../components/VendorBadge';
import { calculateMetricScore, getScoreStatus, calculateTextSimilarityScore } from '../utils/scoringMethodology';
import { getPurchaseTypeIcon } from '../../create-project-flow/components/utils';

// Sanitize vendor website URLs that may contain display names from AI responses
// e.g. "Living Spaces (livingspaces.com)" -> "livingspaces.com"
const sanitizeWebsiteUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw?.trim();
  if (/^https?:\/\//i?.test(trimmed)) return trimmed;
  const parenMatch = trimmed?.match(/\(([^)]+)\)/);
  if (parenMatch) {
    const inner = parenMatch?.[1]?.trim();
    if (/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/?.test(inner)) return inner;
  }
  if (trimmed?.includes(' ')) {
    const tokens = trimmed?.split(/\s+/);
    const domainToken = tokens?.find(t => /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/?.test(t));
    return domainToken || '';
  }
  return trimmed;
};

const ComparisonTable = ({ quotes, vendors, onSelectVendor, selectedVendor, customWeights = null, showCalculations = false, projectNeeds = [], project = null, onRemoveVendor }) => {
  const navigate = useNavigate();
  const [recommendedVendor, setRecommendedVendor] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [hoveredSelectedBtn, setHoveredSelectedBtn] = useState(false);
  const [tooltip, setTooltip] = useState(null); // { vendorName, needId, x, y }

  const calculateReviewScore = (rating, reviewCount, allReviewCounts) => {
    return calculateMetricScore('reviews', rating, { reviewCount, allReviewCounts });
  };

  const getReviewStatus = (rating) => {
    let score = calculateReviewScore(rating);
    return getScoreStatus(score);
  };

  const getVendorAssessment = (vendor, quote, needId) => {
    if (needId === 'reviews') {
      const rating = vendor?.rating;
      const reviewCount = vendor?.reviewCount;
      const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);

      // Collect all review counts across vendors for fair confidence comparison
      const allReviewCounts = quotes
        ?.map(q => q?.vendor?.reviewCount || 0)
        ?.filter(c => c != null);

      if (!rating && !reviewCount) {
        const specs = vendorQuote?.specs || {};
        const reviewScore = specs?.reviewScore != null ? parseFloat(specs?.reviewScore) : 0;
        const reviewCountVal = specs?.reviewCount != null ? parseInt(specs?.reviewCount) : 0;
        const reviewMaxScore = specs?.reviewMaxScore != null ? parseFloat(specs?.reviewMaxScore) : 5;
        if (reviewScore > 0) {
          const normalizedRating = reviewMaxScore > 0 ? (reviewScore / reviewMaxScore) * 5 : reviewScore;
          let score = calculateReviewScore(normalizedRating, reviewCountVal, allReviewCounts);
          return {
            score,
            status: getScoreStatus(score),
            note: `${reviewScore}/${reviewMaxScore}\u2605${reviewCountVal ? ` (${reviewCountVal?.toLocaleString()} reviews)` : ''}`
          };
        }
        return { score: 0, status: 'unknown', note: 'No review data', noData: true };
      }
      const ratingVal = rating || 0;
      const reviewCountVal = reviewCount || 0;
      let score = calculateReviewScore(ratingVal, reviewCountVal, allReviewCounts);
      const status = getScoreStatus(score);
      return {
        score,
        status,
        note: `${ratingVal}\u2605 vendor rating${reviewCountVal ? ` (${reviewCountVal} reviews)` : ''}`
      };
    }

    // ── Service cost fields: totalCost, laborCost, materialCost ──────────────
    const SERVICE_COST_IDS = new Set(['totalCost', 'laborCost', 'materialCost', 'totalAmount', 'estimatedCost', 'projectCost']);
    if (SERVICE_COST_IDS?.has(needId)) {
      const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);
      const rawCost = vendorQuote?.specs?.[needId] ?? vendorQuote?.lineItems?.[needId];
      const vendorCost = rawCost != null ? parseFloat(String(rawCost)?.replace(/[^0-9.]/g, '')) : null;
      if (!vendorCost || isNaN(vendorCost)) return { score: 0, status: 'unknown', note: 'No cost data', noData: true };

      const allCosts = quotes
        ?.map(q => {
          const rc = q?.specs?.[needId] ?? q?.lineItems?.[needId];
          return rc != null ? parseFloat(String(rc)?.replace(/[^0-9.]/g, '')) : null;
        })
        ?.filter(c => c != null && !isNaN(c) && c > 0);

      let score = calculateMetricScore(needId, vendorCost, { allCosts, allPrices: allCosts });
      return { score, status: getScoreStatus(score), note: `$${vendorCost?.toLocaleString()}` };
    }

    // ── Service timeline fields ───────────────────────────────────────────────
    const SERVICE_TIMELINE_IDS = new Set(['timeline', 'completionTimeline', 'projectTimeline', 'startDate']);
    if (SERVICE_TIMELINE_IDS?.has(needId)) {
      const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);
      const timelineVal = vendorQuote?.specs?.[needId] ?? vendorQuote?.lineItems?.[needId] ?? vendorQuote?.deliveryTime;
      if (!timelineVal) return { score: 0, status: 'unknown', note: 'No timeline data', noData: true };
      let score = calculateMetricScore(needId, timelineVal);
      return { score, status: getScoreStatus(score), note: String(timelineVal) };
    }

    // ── Service warranty fields ───────────────────────────────────────────────
    const SERVICE_WARRANTY_IDS = new Set(['warranty', 'guarantee', 'warrantySupport', 'workmanshipWarranty', 'materialWarranty']);
    if (SERVICE_WARRANTY_IDS?.has(needId)) {
      const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);
      const warrantyVal = vendorQuote?.specs?.[needId] ?? vendorQuote?.lineItems?.[needId];
      if (!warrantyVal) return { score: 0, status: 'unknown', note: 'No warranty data', noData: true };
      let score = calculateMetricScore(needId, warrantyVal);
      return { score, status: getScoreStatus(score), note: String(warrantyVal) };
    }

    // Dynamic price scoring using actual quote prices
    if (needId === 'price' || needId === 'budget' || needId === 'cost' || needId === 'priceMax' ||
        needId === 'totalBudget' || needId === 'totalCost' || needId === 'packagePrice' ||
        needId === 'purchasePrice' || needId === 'strollerPrice' || needId === 'tripCost') {
      const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);
      const rawPrice = vendorQuote?.price ?? vendorQuote?.specs?.[needId];
      const vendorPrice = rawPrice != null ? parseFloat(String(rawPrice)?.replace(/[^0-9.]/g, '')) : null;
      if (!vendorPrice || isNaN(vendorPrice)) return { score: 0, status: 'unknown', note: 'No price data', noData: true };

      const allPrices = quotes
        ?.map(q => {
          const rp = q?.price ?? q?.specs?.[needId];
          return rp != null ? parseFloat(String(rp)?.replace(/[^0-9.]/g, '')) : null;
        })
        ?.filter(p => p != null && !isNaN(p) && p > 0);

      const budgetRaw = project?.budget ||
        project?.specifications?.specs?.priceMax ||
        project?.specifications?.specs?.totalBudget ||
        project?.specifications?.specs?.totalCost ||
        project?.specifications?.specs?.purchasePrice ||
        project?.specifications?.specs?.tripCost;
      const userBudget = budgetRaw
        ? parseFloat(String(budgetRaw)?.replace(/[^0-9.]/g, '')) || null
        : null;

      let score = calculateMetricScore('price', vendorPrice, { allPrices, userBudget });
      const status = getScoreStatus(score);

      const minPrice = allPrices?.length > 0 ? Math.min(...allPrices) : vendorPrice;
      const isCheapest = vendorPrice === minPrice;
      const pctAboveCheapest = minPrice > 0 ? Math.round(((vendorPrice - minPrice) / minPrice) * 100) : 0;
      const overBudget = userBudget && vendorPrice > userBudget;

      let note = `$${vendorPrice?.toLocaleString()}`;
      if (isCheapest) note += ' \u2014 best price';
      else if (pctAboveCheapest > 0) note += ` \u2014 ${pctAboveCheapest}% above lowest`;
      if (overBudget) note += ` (${Math.round(((vendorPrice - userBudget) / userBudget) * 100)}% over budget)`;

      return { score, status, note };
    }

    // Dynamic delivery scoring
    if (needId === 'delivery' || needId === 'deliveryTime' || needId === 'shipping') {
      const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);
      const deliveryText = vendorQuote?.deliveryTime || '';
      const dayMatch = deliveryText?.match(/(\d+)/);
      const days = dayMatch ? parseInt(dayMatch?.[1]) : null;
      if (days === null) {
        const isSameDay = deliveryText?.toLowerCase()?.includes('same day');
        let score = isSameDay ? 100 : 70;
        return { score, status: getScoreStatus(score), note: deliveryText };
      }
      let score = calculateMetricScore('delivery', days);
      return { score, status: getScoreStatus(score), note: deliveryText };
    }

    // Dynamic review/reputation scoring
    if (needId === 'reputation' || needId === 'vendorRating') {
      const rating = vendor?.rating;
      const reviewCount = vendor?.reviewCount;
      if (!rating && !reviewCount) return { score: 0, status: 'unknown', note: 'No review data', noData: true };
      const ratingVal = rating || 0;
      const reviewCountVal = reviewCount || 0;
      const allReviewCounts = quotes?.map(q => q?.vendor?.reviewCount || 0)?.filter(c => c != null);
      let score = calculateReviewScore(ratingVal, reviewCountVal, allReviewCounts);
      return { score, status: getScoreStatus(score), note: `${ratingVal}\u2605 (${reviewCountVal?.toLocaleString()} reviews)` };
    }

    // ─── Dynamic spec scoring: read real data from the quote object ───────────
    const vendorQuoteForSpec = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);

    const projectRequirement = project?.specifications?.specs?.[needId];

    const allSpecValues = quotes
      ?.map(q => q?.specs?.[needId])
      ?.filter(v => v != null && String(v)?.trim() !== '');

    // 1. Check the structured specs map (key-value pairs stored in quote.specs)
    //    For service quotes, specs is populated from lineItems quotesheet responses
    if (vendorQuoteForSpec?.specs && vendorQuoteForSpec?.specs?.[needId] != null) {
      const rawValue = vendorQuoteForSpec?.specs?.[needId];
      const specValue = String(rawValue)?.trim();
      if (!specValue) return { score: 0, status: 'unknown', note: 'No data', noData: true };

      const allRawValues = allSpecValues?.map(v => {
        const n = parseFloat(String(v));
        return isNaN(n) ? null : n;
      })?.filter(v => v !== null);

      const numericValue = parseFloat(specValue);
      let score;

      if (!isNaN(numericValue) && allRawValues?.length > 1) {
        const lowerIsBetter = ['leadtime', 'lead_time', 'assemblyrequired', 'assembly_required', 'assembly']?.includes(needId?.toLowerCase());
        const minVal = Math.min(...allRawValues);
        const maxVal = Math.max(...allRawValues);
        const range = maxVal - minVal;

        // Parse the project requirement as a number if available
        const reqNumeric = projectRequirement != null ? parseFloat(String(projectRequirement)?.replace(/[^0-9.]/g, '')) : NaN;

        // If the vendor value exactly meets the project requirement, score 100
        if (!isNaN(reqNumeric) && reqNumeric > 0 && numericValue === reqNumeric) {
          score = 100;
        } else if (range === 0) {
          // All options have the same value — score 100 (all equally meet criteria)
          score = 100;
        } else {
          // Check if ALL options meet the requirement — if so, floor at 80
          // (all satisfy the requirement, just some are better than others)
          const allMeetReq = !isNaN(reqNumeric) && reqNumeric > 0 && allRawValues?.every(v =>
            lowerIsBetter ? v <= reqNumeric : v >= reqNumeric
          );

          // Relative scoring among options that differ
          const position = (numericValue - minVal) / range;
          score = lowerIsBetter
            ? Math.round(100 - position * 50)
            : Math.round(50 + position * 50);

          // If this vendor meets the requirement exactly, boost to 100
          if (!isNaN(reqNumeric) && reqNumeric > 0) {
            const meetsReq = lowerIsBetter ? numericValue <= reqNumeric : numericValue >= reqNumeric;
            if (meetsReq && numericValue === reqNumeric) {
              score = 100;
            }
          }

          // If all options meet the requirement, no option should score below 80
          if (allMeetReq) {
            score = Math.max(80, score);
          }
        }
      } else if (projectRequirement && typeof specValue === 'string' && isNaN(numericValue)) {
        const simScore = calculateTextSimilarityScore(specValue, String(projectRequirement));
        score = simScore > 0 ? simScore : 70;
      } else {
        const engineScore = calculateMetricScore(needId, specValue, {
          allValues: allRawValues?.length > 0 ? allRawValues : undefined,
          requirement: projectRequirement != null ? projectRequirement : undefined
        });
        score = engineScore > 0 ? engineScore : 70;
      }

      return { score, status: getScoreStatus(score), note: specValue };
    }

    // 1b. Fallback for qualitative needs
    if (projectRequirement && vendorQuoteForSpec) {
      const searchText = [
        vendorQuoteForSpec?.vendor?.name || '',
        vendorQuoteForSpec?.terms || '',
        ...(vendorQuoteForSpec?.specifications || [])
      ]?.join(' ')?.toLowerCase();

      const reqText = String(projectRequirement)?.toLowerCase()?.trim();
      if (reqText && searchText) {
        if (searchText?.includes(reqText)) {
          let score = 90;
          return { score, status: getScoreStatus(score), note: `Matches: ${projectRequirement}` };
        }
        const reqWords = reqText?.split(/\s+/)?.filter(w => w?.length > 2);
        const matchCount = reqWords?.filter(w => searchText?.includes(w))?.length;
        if (reqWords?.length > 0 && matchCount > 0) {
          const overlap = matchCount / reqWords?.length;
          let score = Math.round(50 + overlap * 45);
          return { score, status: getScoreStatus(score), note: `Partial match: ${projectRequirement}` };
        }
      }
      return { score: 0, status: 'unknown', note: 'No spec data for this option', noData: true };
    }

    // 2. Search the free-text specifications array
    if (Array.isArray(vendorQuoteForSpec?.specifications) && vendorQuoteForSpec?.specifications?.length > 0) {
      const needLabel = needId?.toLowerCase();
      const needWords = needId?.replace(/([A-Z])/g, ' $1')?.toLowerCase()?.trim();
      const matchingSpec = vendorQuoteForSpec?.specifications?.find(spec => {
        const s = spec?.toLowerCase();
        return s?.includes(needLabel) || s?.includes(needWords);
      });
      if (matchingSpec) {
        const numMatch = matchingSpec?.match(/([\d.]+)/);
        if (numMatch) {
          const numVal = parseFloat(numMatch?.[1]);
          const engineScore = calculateMetricScore(needId, numVal);
          let score = engineScore > 0 ? engineScore : 75;
          return { score, status: getScoreStatus(score), note: matchingSpec };
        }
        return { score: 75, status: getScoreStatus(75), note: matchingSpec };
      }
      return { score: 0, status: 'unknown', note: 'Not specified', noData: true };
    }

    // 3. No spec data available
    return { score: 0, status: 'unknown', note: 'No data', noData: true };
  };

  const getProductName = (vendorName) => {
    const quote = quotes?.find(q => q?.vendor?.name === vendorName);
    return quote?.specifications?.[0] || 'Product';
  };

  const getProductLink = (vendorName) => {
    const quote = quotes?.find(q => q?.vendor?.name === vendorName);
    // Prefer the specific product URL returned by Perplexity over the vendor root domain
    const productUrl = quote?.specs?.productUrl || quote?.lineItems?.productUrl || '';
    if (productUrl) {
      return productUrl?.startsWith('http') ? productUrl : `https://${productUrl}`;
    }
    const rawWebsite = quote?.vendor?.website;
    if (!rawWebsite) return '#';
    const cleaned = sanitizeWebsiteUrl(rawWebsite);
    if (!cleaned) return '#';
    return cleaned?.startsWith('http') ? cleaned : `https://${cleaned}`;
  };

  const getQuoteForVendor = (vendorName) => {
    return quotes?.find(q => q?.vendor?.name === vendorName);
  };

  const calculateOverallScore = (vendor) => {
    if (customWeights) {
      const validNeeds = projectNeeds?.filter(need => {
        const assessment = getVendorAssessment(vendor, null, need?.id);
        return !assessment?.noData;
      });
      if (!validNeeds?.length) return 0;
      const totalWeight = validNeeds?.reduce((sum, need) => sum + (customWeights?.[need?.id] || 0), 0);
      if (!totalWeight) return 0;
      const scores = validNeeds?.map(need => {
        const assessment = getVendorAssessment(vendor, null, need?.id);
        const weight = customWeights?.[need?.id] || 0;
        return assessment?.score * (weight / totalWeight);
      });
      return Math.round(scores?.reduce((sum, score) => sum + score, 0) * 100) / 100 | 0;
    }
    
    const validNeeds = projectNeeds?.filter(need => {
      const assessment = getVendorAssessment(vendor, null, need?.id);
      return !assessment?.noData;
    });
    if (!validNeeds?.length) return 0;

    const scores = validNeeds?.map(need => {
      const assessment = getVendorAssessment(vendor, null, need?.id);
      const weight = need?.priority === 'High' ? 3 : need?.priority === 'Medium' ? 2 : 1;
      return assessment?.score * weight;
    });
    
    const totalWeight = validNeeds?.reduce((sum, need) => 
      sum + (need?.priority === 'High' ? 3 : need?.priority === 'Medium' ? 2 : 1), 0
    );
    
    return Math.round(scores?.reduce((sum, score) => sum + score, 0) / totalWeight);
  };

  const getBestRecommendation = () => {
    if (!vendors?.length) return null;
    return vendors?.reduce((best, current) => {
      const currentScore = calculateOverallScore(current);
      const bestScore = best ? calculateOverallScore(best) : 0;
      return currentScore > bestScore ? current : best;
    }, null);
  };

  const bestVendor = getBestRecommendation();

  const handleSelectRecommended = () => {
    if (bestVendor) {
      onSelectVendor?.(bestVendor);
      setRecommendedVendor(bestVendor);
    }
  };

  // Product-type subcategory IDs — purchases fulfilled on third-party retailer sites
  const PRODUCT_CATEGORY_IDS = new Set([
    'furniture', 'mattresses', 'major-appliances', 'home-gym', 'home-security',
    'computers-laptops', 'tablets', 'smartphones', 'tv-home-theater',
    'smart-home-devices', 'audio-equipment', 'headphones', 'wearables',
    'car-purchase', 'motorcycle', 'rv', 'boat', 'tires',
    'engagement-rings', 'baby-products', 'medical-equipment'
  ]);

  const isProductCategory = PRODUCT_CATEGORY_IDS?.has(project?.purchaseType);

  const handleProceedWithVendor = () => {
    if (!selectedVendor) return;
    const matchingQuote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
    // Check all possible locations where productUrl may be stored
    const productUrl =
      matchingQuote?.productUrl ||
      matchingQuote?.specs?.productUrl ||
      matchingQuote?.lineItems?.productUrl ||
      '';
    if (productUrl) {
      const url = productUrl?.startsWith('http') ? productUrl : `https://${productUrl}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Fallback to vendor website
    const rawUrl = matchingQuote?.vendor?.website || selectedVendor?.website;
    const vendorUrl = sanitizeWebsiteUrl(rawUrl || '');
    if (vendorUrl) {
      const url = vendorUrl?.startsWith('http') ? vendorUrl : `https://${vendorUrl}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Last resort: search for the vendor online
      const searchQuery = encodeURIComponent(selectedVendor?.name);
      window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
    }
  };

  // Score ring color
  const getScoreRingColor = (score) => {
    if (score >= 90) return '#22c55e';
    if (score >= 75) return '#3b82f6';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  // Criteria icon gradient badge
  const getCriteriaGradient = (priority) => {
    if (priority === 'High') return 'linear-gradient(135deg, #F58A54, #F06F6B)';
    if (priority === 'Medium') return 'linear-gradient(135deg, #F06F6B, #E0528A)';
    return 'linear-gradient(135deg, #a78bfa, #818cf8)';
  };

  const getScorePillStyle = (status) => {
    switch (status) {
      case 'excellent': return { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' };
      case 'good': return { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' };
      case 'fair': return { bg: '#fffbeb', text: '#b45309', border: '#fde68a' };
      case 'poor': return { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' };
      default: return { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };
    }
  };

  const needIconColors = [
    'linear-gradient(135deg, #F58A54, #F06F6B)','linear-gradient(135deg, #F06F6B, #E0528A)','linear-gradient(135deg, #E0528A, #c026d3)','linear-gradient(135deg, #f59e0b, #F58A54)','linear-gradient(135deg, #06b6d4, #3b82f6)','linear-gradient(135deg, #8b5cf6, #E0528A)','linear-gradient(135deg, #10b981, #06b6d4)',
  ];

  // Data quality indicator: determines how reliable/complete the score data is
  const getDataQuality = (vendor, quote, needId, assessment) => {
    if (assessment?.noData) return null;

    const vendorQuote = quote || quotes?.find(q => q?.vendor?.name === vendor?.name);
    let note = assessment?.note || '';

    // Full info: direct spec value found in quote.specs
    if (vendorQuote?.specs && vendorQuote?.specs?.[needId] != null) {
      const rawValue = vendorQuote?.specs?.[needId];
      if (String(rawValue)?.trim()) return 'full';
    }

    // Full info: known structured fields with direct data
    const SERVICE_COST_IDS = new Set(['totalCost', 'laborCost', 'materialCost', 'totalAmount', 'estimatedCost', 'projectCost']);
    const PRICE_IDS = new Set(['price', 'budget', 'cost', 'priceMax', 'totalBudget', 'packagePrice', 'purchasePrice', 'strollerPrice', 'tripCost']);
    if ((SERVICE_COST_IDS?.has(needId) || PRICE_IDS?.has(needId)) && note && !note?.includes('No')) return 'full';
    if (needId === 'reviews' && vendor?.rating != null) return 'full';
    if ((needId === 'delivery' || needId === 'deliveryTime' || needId === 'shipping') && vendorQuote?.deliveryTime) return 'full';

    // Partial match: text/keyword match from free-text search
    if (note?.includes('Matches:') || note?.includes('Partial match:')) return 'partial';
    if (Array.isArray(vendorQuote?.specifications) && vendorQuote?.specifications?.length > 0) {
      const needLabel = needId?.toLowerCase();
      const needWords = needId?.replace(/([A-Z])/g, ' $1')?.toLowerCase()?.trim();
      const matchingSpec = vendorQuote?.specifications?.find(spec => {
        const s = spec?.toLowerCase();
        return s?.includes(needLabel) || s?.includes(needWords);
      });
      if (matchingSpec) return 'partial';
    }

    // Estimated: score was derived but no direct data
    if (assessment?.score > 0) return 'estimated';

    return null;
  };

  const DATA_QUALITY_CONFIG = {
    full:      { label: 'Full info',      icon: 'CheckCircle2', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
    partial:   { label: 'Partial match',  icon: 'AlertCircle',  color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
    estimated: { label: 'Estimated',      icon: 'HelpCircle',   color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
  };

  const getTooltipText = (vendor, need, assessment) => {
    const req = project?.specifications?.specs?.[need?.id];
    const reqLabel = req != null ? String(req) : null;
    let score = assessment?.score;
    let note = assessment?.note;

    if (assessment?.noData) return `No data available for ${need?.name}`;

    let lines = [];

    // Score label
    if (score >= 90) lines?.push(`✅ Excellent match (${score}%)`);
    else if (score >= 75) lines?.push(`🔵 Good match (${score}%)`);
    else if (score >= 60) lines?.push(`🟡 Fair match (${score}%)`);
    else lines?.push(`🔴 Needs improvement (${score}%)`);

    // Requirement context
    if (reqLabel) lines?.push(`Requirement: ${reqLabel}`);

    // Vendor value from note
    if (note && note !== reqLabel) lines?.push(`Vendor: ${note}`);

    // Scoring rationale
    if (score === 100 && reqLabel) {
      lines?.push('Exactly meets your requirement.');
    } else if (score === 100) {
      lines?.push('All options are equal — full score awarded.');
    } else if (note?.includes('best price')) {
      lines?.push('Lowest price among all options.');
    } else if (note?.includes('above lowest')) {
      lines?.push('Higher than the lowest-priced option.');
    } else if (note?.includes('over budget')) {
      lines?.push('Exceeds your stated budget.');
    } else if (note?.includes('Matches:')) {
      lines?.push('Vendor description matches your requirement.');
    } else if (note?.includes('Partial match:')) {
      lines?.push('Partially matches your requirement keywords.');
    } else if (score < 100 && reqLabel) {
      lines?.push('Does not fully match your stated requirement.');
    }

    return lines?.join('\n');
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #fff0f5 50%, #f5f0ff 100%)', border: '1.5px solid #fde8d8' }}
      >
        <div className="px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="LayoutGrid" size={20} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Side-by-Side Comparison</h3>
              <p className="text-sm text-gray-500">
                {showCalculations
                  ? 'Scores calculated from your custom weights' :'Set your weights above to unlock personalized scores'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {bestVendor && showCalculations && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}>
                <Icon name="Crown" size={13} color="white" />
                <span className="text-xs font-bold text-white tracking-wide">TOP PICK</span>
              </div>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-gray-500" />
            </button>
          </div>
        </div>
      </div>
      {isExpanded && (
        <>
          {/* Vendor Product Cards */}
          <div className={`grid gap-5 ${
            vendors?.length === 1 ? 'grid-cols-1 max-w-sm' :
            vendors?.length === 2 ? 'grid-cols-1 md:grid-cols-2': 'grid-cols-1 md:grid-cols-3'
          }`}>
            {vendors?.map((vendor, index) => {
              const quote = getQuoteForVendor(vendor?.name);
              const overallScore = calculateOverallScore(vendor);
              const isWinner = bestVendor?.name === vendor?.name && showCalculations;
              const isSelected = selectedVendor?.name === vendor?.name;
              const scoreColor = getScoreRingColor(overallScore);

              return (
                <div
                  key={index}
                  className="relative rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                  style={{
                    border: isSelected
                      ? '2.5px solid #F58A54'
                      : isWinner
                      ? '2.5px solid #F06F6B' :'1.5px solid #e5e7eb',
                    boxShadow: isSelected
                      ? '0 8px 32px rgba(245,138,84,0.18)'
                      : isWinner
                      ? '0 8px 24px rgba(240,111,107,0.14)'
                      : '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                  onClick={() => onSelectVendor?.(vendor)}
                >
                  {/* Winner / Selected ribbon */}
                  {isWinner && (
                    <div
                      className="absolute top-0 left-0 right-0 py-1.5 flex items-center justify-center gap-1.5 z-10"
                      style={{ background: 'linear-gradient(90deg, #F58A54, #F06F6B, #E0528A)' }}
                    >
                      <Icon name="Crown" size={13} color="white" />
                      <span className="text-xs font-bold text-white tracking-wide">TOP PICK</span>
                    </div>
                  )}
                  {isSelected && !isWinner && (
                    <div
                      className="absolute top-0 left-0 right-0 py-1.5 flex items-center justify-center gap-1.5 z-10"
                      style={{ background: 'linear-gradient(90deg, #F58A54, #E0528A)' }}
                    >
                      <Icon name="CheckCircle2" size={13} color="white" />
                      <span className="text-xs font-bold text-white tracking-wide">YOUR PICK</span>
                    </div>
                  )}
                  <div className={`bg-white ${isWinner || isSelected ? 'pt-9' : 'pt-5'} px-5 pb-5`}>
                    {/* Product Image + Score Ring */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {vendor?.logo ? (
                          <img
                            src={vendor?.logo}
                            alt={vendor?.logoAlt || `${vendor?.name} logo`}
                            className="w-14 h-14 rounded-xl object-cover border border-gray-100"
                            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                          />
                        ) : (
                          <div
                            className="w-14 h-14 rounded-xl flex items-center justify-center"
                            style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                          >
                            <Icon name="Store" size={24} color="white" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-bold text-gray-900 text-base">{vendor?.name}</h4>
                          {(() => {
                            // Prefer vendor-level rating; fall back to quote.specs for AI-suggested options
                            const specs = quote?.specs || {};
                            const displayRating = vendor?.rating != null
                              ? vendor?.rating
                              : specs?.reviewScore != null
                                ? `${specs?.reviewScore}/${specs?.reviewMaxScore ?? 5}`
                                : null;
                            const displayCount = vendor?.reviewCount != null
                              ? vendor?.reviewCount
                              : specs?.reviewCount != null
                                ? parseInt(specs?.reviewCount)
                                : null;
                            if (displayRating == null && displayCount == null) return null;
                            return (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Icon name="Star" size={13} className="text-amber-400" />
                                <span className="text-sm font-semibold text-gray-700">{displayRating}</span>
                                {displayCount != null && (
                                  <span className="text-xs text-gray-400">({displayCount?.toLocaleString()})</span>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        {/* Remove button */}
                        <button
                          className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 hover:bg-red-50 group"
                          style={{ border: '1px solid #fecaca' }}
                          title="Remove from comparison"
                          onClick={e => { e?.stopPropagation(); onRemoveVendor?.(vendor?.name); }}
                        >
                          <Icon name="Trash2" size={14} className="text-red-400 group-hover:text-red-600" />
                        </button>

                        {/* Score Circle */}
                        {showCalculations && (
                          <div className="flex flex-col items-center">
                            <div
                              className="w-14 h-14 rounded-full flex items-center justify-center"
                              style={{
                                background: `conic-gradient(${scoreColor} ${overallScore * 3.6}deg, #f3f4f6 0deg)`,
                                padding: '3px',
                              }}
                            >
                              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                                <span className="text-sm font-bold" style={{ color: scoreColor }}>{overallScore}</span>
                              </div>
                            </div>
                            <span className="text-xs text-gray-400 mt-1">Score</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Vendor Icon Avatar */}
                    {(() => {
                      const hue = (vendor?.name || '')?.charCodeAt(0) * 37 % 360;
                      const iconName = getPurchaseTypeIcon(project?.purchaseType) || 'ShoppingCart';
                      const imageUrl = quote?.specs?.imageUrl || '';
                      if (imageUrl) {
                        return (
                          <img
                            src={imageUrl}
                            alt={`${vendor?.name || ''} photo`}
                            className="w-full h-40 rounded-xl object-contain mb-3 bg-gray-50"
                            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e?.currentTarget?.nextSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        );
                      }
                      return (
                        <div
                          className="w-full h-40 rounded-xl flex items-center justify-center mb-3 text-white"
                          style={{ background: `hsl(${hue}, 55%, 52%)` }}
                        >
                          <Icon name={iconName} size={32} color="white" />
                        </div>
                      );
                    })()}

                    {/* Price + Delivery */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <span className="text-2xl font-bold text-gray-900">${quote?.price?.toLocaleString()}</span>
                        {quote?.originalPrice && (
                          <span className="text-sm text-gray-400 line-through ml-2">${quote?.originalPrice?.toLocaleString()}</span>
                        )}
                      </div>
                      <div
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}
                      >
                        <Icon name="Truck" size={11} />
                        <span>{quote?.deliveryTime}</span>
                      </div>
                    </div>

                    {/* Criteria Scores */}
                    <div className="space-y-2.5 mb-4">
                      {projectNeeds?.slice(0, activeTab === vendor?.name ? projectNeeds?.length : 4)?.map((need, needIdx) => {
                        const assessment = getVendorAssessment(vendor, quote, need?.id);
                        const pillStyle = getScorePillStyle(assessment?.status);
                        const tooltipKey = `${vendor?.name}-${need?.id}`;
                        const dqKey = `dq-${vendor?.name}-${need?.id}`;
                        const dataQuality = getDataQuality(vendor, quote, need?.id, assessment);
                        const dqConfig = dataQuality ? DATA_QUALITY_CONFIG?.[dataQuality] : null;
                        return (
                          <div key={need?.id} className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ background: needIconColors?.[needIdx % needIconColors?.length] }}
                            >
                              <Icon name={need?.icon} size={12} color="white" />
                            </div>
                            <span className="text-xs text-gray-600 flex-1 truncate">{need?.name}</span>
                            <div className="flex items-center gap-1.5">
                              {assessment?.noData ? (
                                <span
                                  className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                                  style={{ background: '#f9fafb', color: '#9ca3af', border: '1px solid #e5e7eb' }}
                                >
                                  -
                                </span>
                              ) : (
                                <>
                                  <div
                                    className="h-1.5 rounded-full"
                                    style={{
                                      width: `${Math.max(assessment?.score * 0.5, 4)}px`,
                                      background: getScoreRingColor(assessment?.score),
                                      minWidth: '4px',
                                      maxWidth: '50px',
                                    }}
                                  />
                                  {/* Data Quality Badge */}
                                  {dqConfig && (
                                    <div className="relative" style={{ display: 'inline-block' }}>
                                      <div
                                        className="w-4 h-4 rounded-full flex items-center justify-center cursor-help flex-shrink-0"
                                        style={{ background: dqConfig?.bg, border: `1px solid ${dqConfig?.border}` }}
                                        onMouseEnter={() => setTooltip(dqKey)}
                                        onMouseLeave={() => setTooltip(null)}
                                      >
                                        <Icon name={dqConfig?.icon} size={9} color={dqConfig?.color} />
                                      </div>
                                      {tooltip === dqKey && (
                                        <div
                                          className="absolute z-50 right-0 bottom-full mb-2 rounded-lg shadow-xl pointer-events-none whitespace-nowrap"
                                          style={{
                                            background: 'linear-gradient(135deg, #1e1b2e 0%, #2d1f3d 100%)',
                                            border: '1px solid rgba(245,138,84,0.3)',
                                            padding: '6px 10px',
                                          }}
                                        >
                                          <div
                                            className="absolute right-1.5 top-full"
                                            style={{
                                              width: 0, height: 0,
                                              borderLeft: '5px solid transparent',
                                              borderRight: '5px solid transparent',
                                              borderTop: '5px solid #2d1f3d',
                                            }}
                                          />
                                          <div className="flex items-center gap-1.5">
                                            <div
                                              className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                              style={{ background: dqConfig?.bg, border: `1px solid ${dqConfig?.border}` }}
                                            >
                                              <Icon name={dqConfig?.icon} size={9} color={dqConfig?.color} />
                                            </div>
                                            <span className="text-xs font-semibold" style={{ color: '#fde8d8' }}>{dqConfig?.label}</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <div className="relative" style={{ display: 'inline-block' }}>
                                    <span
                                      className="text-xs font-semibold px-1.5 py-0.5 rounded-full cursor-help"
                                      style={{ background: pillStyle?.bg, color: pillStyle?.text, border: `1px solid ${pillStyle?.border}` }}
                                      onMouseEnter={() => setTooltip(tooltipKey)}
                                      onMouseLeave={() => setTooltip(null)}
                                    >
                                      {assessment?.score}%
                                    </span>
                                    {tooltip === tooltipKey && (
                                      <div
                                        className="absolute z-50 right-0 bottom-full mb-2 w-56 rounded-xl shadow-xl text-left pointer-events-none"
                                        style={{
                                          background: 'linear-gradient(135deg, #1e1b2e 0%, #2d1f3d 100%)',
                                          border: '1px solid rgba(245,138,84,0.3)',
                                          padding: '10px 12px',
                                        }}
                                      >
                                        {/* Arrow */}
                                        <div
                                          className="absolute right-3 top-full"
                                          style={{
                                            width: 0,
                                            height: 0,
                                            borderLeft: '6px solid transparent',
                                            borderRight: '6px solid transparent',
                                            borderTop: '6px solid #2d1f3d',
                                          }}
                                        />
                                        {/* Header */}
                                        <div className="flex items-center gap-1.5 mb-2 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                          <div
                                            className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                                            style={{ background: needIconColors?.[needIdx % needIconColors?.length] }}
                                          >
                                            <Icon name={need?.icon} size={10} color="white" />
                                          </div>
                                          <span className="text-xs font-bold text-white truncate">{need?.name}</span>
                                          <span
                                            className="ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                                            style={{ background: pillStyle?.bg, color: pillStyle?.text }}
                                          >
                                            {assessment?.score}%
                                          </span>
                                        </div>
                                        {/* Body */}
                                        <div className="space-y-1.5">
                                          {getTooltipText(vendor, need, assessment)?.split('\n')?.map((line, i) => (
                                            <p key={i} className="text-xs leading-relaxed" style={{ color: i === 0 ? '#fde8d8' : 'rgba(255,255,255,0.65)' }}>
                                              {line}
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {projectNeeds?.length > 4 && (
                      <button
                        className="text-xs font-medium mb-4 flex items-center gap-1"
                        style={{ color: '#F58A54' }}
                        onClick={e => { e?.stopPropagation(); setActiveTab(activeTab === vendor?.name ? null : vendor?.name); }}
                      >
                        <Icon name={activeTab === vendor?.name ? 'ChevronUp' : 'ChevronDown'} size={13} />
                        {activeTab === vendor?.name ? 'Show less' : `+${projectNeeds?.length - 4} more criteria`}
                      </button>
                    )}

                    {/* Terms */}
                    <div
                      className="text-xs text-gray-500 px-3 py-2 rounded-lg mb-4"
                      style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
                    >
                      <Icon name="FileText" size={11} className="inline mr-1 text-gray-400" />
                      {quote?.terms}
                    </div>

                    {/* Badges */}
                    <div className="flex justify-start mb-4">
                      <VendorBadgeCollection vendor={vendor} maxBadges={3} size="xs" />
                    </div>

                    {/* Select Button */}
                    <button
                      className="w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2"
                      style={{
                        background: isSelected
                          ? 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)'
                          : 'white',
                        color: isSelected ? 'white' : '#F58A54',
                        border: isSelected ? 'none' : '2px solid #F58A54',
                        boxShadow: isSelected ? '0 4px 14px rgba(245,138,84,0.35)' : 'none',
                      }}
                      onMouseEnter={() => isSelected && setHoveredSelectedBtn(true)}
                      onMouseLeave={() => setHoveredSelectedBtn(false)}
                      onClick={e => {
                        e?.stopPropagation();
                        if (isSelected) {
                          // Open product URL
                          const matchingQuote = quotes?.find(q => q?.vendor?.name === vendor?.name);
                          const productUrl =
                            matchingQuote?.productUrl ||
                            matchingQuote?.specs?.productUrl ||
                            matchingQuote?.lineItems?.productUrl ||
                            '';
                          if (productUrl) {
                            const url = productUrl?.startsWith('http') ? productUrl : `https://${productUrl}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } else {
                            const rawUrl = matchingQuote?.vendor?.website || vendor?.website;
                            const vendorUrl = sanitizeWebsiteUrl(rawUrl || '');
                            if (vendorUrl) {
                              const url = vendorUrl?.startsWith('http') ? vendorUrl : `https://${vendorUrl}`;
                              window.open(url, '_blank', 'noopener,noreferrer');
                            } else {
                              const searchQuery = encodeURIComponent(vendor?.name);
                              window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
                            }
                          }
                        } else {
                          onSelectVendor?.(vendor);
                        }
                      }}
                    >
                      <Icon name={isSelected ? (hoveredSelectedBtn ? 'ExternalLink' : 'CheckCircle2') : 'ShoppingBag'} size={15} />
                      {isSelected ? (hoveredSelectedBtn ? 'Proceed with Vendor' : 'Selected!') : 'Choose This'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <div className="flex items-center gap-2 text-xs text-gray-400 px-1">
            <Icon name="Info" size={13} />
            <span>Scores are calculated based on your stack requirements and vendor capabilities</span>
          </div>
        </>
      )}
    </div>
  );
};

export default ComparisonTable;